import type { Language, Weekday } from '../Models/enums';

/** Dates and times, formatted per locale. Never toISOString for a local day. */

export function toDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

export function isValidDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

export function toTime(value: unknown): string | null {
  if (typeof value !== 'string' || !/^\d{2}:\d{2}$/.test(value)) return null;
  const [hours, minutes] = value.split(':').map(Number);
  if (hours === undefined || minutes === undefined) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

const LOCALES: Record<Language, string> = { he: 'he-IL', en: 'en-GB' };

export function formatDate(date: string, language: Language): string {
  return toDate(date).toLocaleDateString(LOCALES[language], {
    day: 'numeric', month: 'numeric', year: 'numeric'
  });
}

export function formatDateLong(date: string, language: Language): string {
  return toDate(date).toLocaleDateString(LOCALES[language], {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

export function formatMonth(month: string, language: Language): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(year ?? 1970, (monthNumber ?? 1) - 1, 1)
    .toLocaleDateString(LOCALES[language], { month: 'long', year: 'numeric' });
}

/** Adds days without crossing a daylight-saving boundary incorrectly. */
export function addDays(date: string, days: number): string {
  const at = toDate(date);
  at.setDate(at.getDate() + days);
  const year = at.getFullYear();
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const day = String(at.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** The Sunday of the week a date falls in. The tutor's week starts on Sunday. */
export function startOfWeek(date: string): string {
  return addDays(date, -toDate(date).getDay());
}

export function monthKey(date: string): string {
  return date.slice(0, 7);
}

export const WEEKDAYS: readonly Weekday[] = [0, 1, 2, 3, 4, 5, 6];
